import { useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { TENANT_ROLES, type ManagedUser } from '../api/types.js';
import { useApi, useAuth } from '../auth.js';
import { CopyBlock, ErrorBox, Spinner } from '../components.js';
import { ConfirmDialog } from '../EdgeLifecycle.js';
import { useNow, useResource } from '../hooks.js';
import { formatAge, formatTimestamp } from '../lib/format.js';
import { adminTenants } from '../lib/permissions.js';
import {
  generatePassword,
  MIN_PASSWORD_LENGTH,
  newPasswordError,
  removalBlocked,
  ROLE_HELP,
  ROLE_LABELS,
  roleSummary,
  rolesBody,
  rolesError,
  rolesOf,
  sortUsers,
  userErrorText,
  type RolesValue,
} from '../lib/users.js';

/** A tenant the caller can give roles in, with the sites it may hand out. */
interface TenantOption {
  id: string;
  name: string;
  sites: { id: string; name: string }[];
  /** The caller has every site of the tenant (may give "all sites"). */
  allSites: boolean;
}

/** Tenants for the role editor: all (platform staff) or those the caller administers. */
function useTenantOptions(): { options: TenantOption[] | undefined; platform: boolean } {
  const api = useApi();
  const { user: me } = useAuth();
  const platform = me?.permissions.platform ?? false;
  const res = useResource(`role-tenants:${platform ? 'all' : 'mine'}`, async (signal) => {
    if (platform) {
      const tenants = await api.tenants(signal);
      return tenants.map((t) => ({ id: t.id, name: t.name, sites: t.sites, allSites: true }));
    }
    const sites = await api.sites();
    return adminTenants(me).map((t) => ({
      ...t,
      sites: sites.filter((s) => s.tenant.id === t.id).map((s) => ({ id: s.id, name: s.name })),
    }));
  });
  return { options: res.data, platform };
}

/** Platform role and tenant roles (with sites) of one user. */
function RolesEditor({
  value,
  onChange,
  options,
  platform,
}: {
  value: RolesValue;
  onChange: (v: RolesValue) => void;
  options: TenantOption[];
  /** The caller is platform staff (may give the platform role). */
  platform: boolean;
}) {
  const setTenant = (id: string, t: RolesValue['tenants'][string]) => {
    onChange({ ...value, tenants: { ...value.tenants, [id]: t } });
  };
  return (
    <div className="roles-editor">
      {platform && (
        <label className="check">
          <input
            type="checkbox"
            checked={value.platform}
            onChange={(e) => {
              onChange({ ...value, platform: e.target.checked });
            }}
          />
          <span>
            <strong>Platform admin</strong>
            <span className="muted small">
              {' '}
              IDevelopment staff: every tenant and infrastructure
            </span>
          </span>
        </label>
      )}
      {options.map((t) => {
        const cur = value.tenants[t.id];
        return (
          <fieldset key={t.id} className="roles-tenant">
            <legend>{t.name}</legend>
            <label className="field">
              <span>Role</span>
              <select
                value={cur?.role ?? ''}
                onChange={(e) => {
                  const role = TENANT_ROLES.find((r) => r === e.target.value);
                  setTenant(
                    t.id,
                    role ? { role, siteIds: cur?.siteIds ?? (t.allSites ? null : []) } : undefined,
                  );
                }}
              >
                <option value="">No access</option>
                {TENANT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}: {ROLE_HELP[r]}
                  </option>
                ))}
              </select>
            </label>
            {cur && (
              <div className="roles-sites">
                {t.allSites && (
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={cur.siteIds === null}
                      onChange={(e) => {
                        setTenant(t.id, { ...cur, siteIds: e.target.checked ? null : [] });
                      }}
                    />
                    <span>All sites of {t.name} (also future ones)</span>
                  </label>
                )}
                {cur.siteIds !== null &&
                  t.sites.map((site) => (
                    <label key={site.id} className="check">
                      <input
                        type="checkbox"
                        checked={cur.siteIds?.includes(site.id) ?? false}
                        onChange={(e) => {
                          const ids = new Set(cur.siteIds ?? []);
                          if (e.target.checked) ids.add(site.id);
                          else ids.delete(site.id);
                          setTenant(t.id, { ...cur, siteIds: [...ids] });
                        }}
                      />
                      <span>{site.name}</span>
                    </label>
                  ))}
                {cur.siteIds !== null && t.sites.length === 0 && (
                  <p className="muted small">The tenant has no sites yet.</p>
                )}
              </div>
            )}
          </fieldset>
        );
      })}
      {options.length === 0 && !platform && (
        <p className="muted small">You do not administer a tenant.</p>
      )}
    </div>
  );
}

/** A new user starts as viewer of the caller's only tenant, else without a role. */
function initialRoles(options: TenantOption[] | undefined, platform: boolean): RolesValue {
  const only = !platform && options?.length === 1 ? options[0] : undefined;
  const tenants: RolesValue['tenants'] = {};
  if (only) tenants[only.id] = { role: 'viewer', siteIds: only.allSites ? null : [] };
  return { platform: false, tenants };
}

/** Shown once: the credentials to hand over (the password is not stored in the UI). */
interface Handover {
  title: string;
  email: string;
  password: string;
}

function HandoverPanel({ h, onClose }: { h: Handover; onClose: () => void }) {
  return (
    <div className="card token-created" role="status">
      <div className="section-head">
        <h2>{h.title}</h2>
        <button type="button" className="btn btn-small" onClick={onClose}>
          Done
        </button>
      </div>
      <p className="warn strong">
        Copy the password now and give it to the user over a safe channel. It is not shown again;
        the user can change it under Change password.
      </p>
      <CopyBlock label="E-mail" text={h.email} />
      <CopyBlock label="Password" text={h.password} />
    </div>
  );
}

/** A password input with a Generate button (visible text, so it can be copied). */
function PasswordField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const problem = value ? newPasswordError(value) : null;
  return (
    <div className="field">
      <span>{label}</span>
      <div className="form-actions">
        <input
          type="text"
          className="mono grow"
          autoComplete="off"
          spellCheck={false}
          minLength={MIN_PASSWORD_LENGTH}
          aria-label={label}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
        <button
          type="button"
          className="btn"
          onClick={() => {
            onChange(generatePassword());
          }}
        >
          Generate
        </button>
      </div>
      {problem && <p className="form-error">{problem}</p>}
    </div>
  );
}

function AddUserForm({
  onCreated,
  options,
  platform,
}: {
  onCreated: (u: ManagedUser, password: string) => void;
  options: TenantOption[];
  platform: boolean;
}) {
  const api = useApi();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState(() => generatePassword());
  const [roles, setRoles] = useState<RolesValue>(() => initialRoles(options, platform));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rolesProblem = rolesError(roles);

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const u = await api.createUser({
        email: email.trim(),
        password,
        ...(name.trim() && { name: name.trim() }),
        ...rolesBody(roles, platform),
      });
      onCreated(u, password);
      setEmail('');
      setName('');
      setPassword(generatePassword());
      setRoles(initialRoles(options, platform));
    } catch (err) {
      setError(userErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="card token-form"
      onSubmit={(e) => {
        void submit(e);
      }}
    >
      <div className="edge-form-row">
        <label className="field">
          <span>E-mail</span>
          <input
            type="email"
            autoComplete="off"
            required
            maxLength={320}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span>Name (optional)</span>
          <input
            type="text"
            maxLength={200}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
      </div>
      <PasswordField label="Initial password" value={password} onChange={setPassword} />
      <RolesEditor value={roles} onChange={setRoles} options={options} platform={platform} />
      <p className="muted small">
        {rolesProblem ?? 'The user can change the password after signing in.'}
      </p>
      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}
      <div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={
            busy ||
            !email.includes('@') ||
            newPasswordError(password) !== null ||
            rolesProblem !== null
          }
        >
          {busy ? 'Adding…' : 'Add user'}
        </button>
      </div>
    </form>
  );
}

type Action = 'disable' | 'enable' | 'delete' | 'reset' | 'rename' | 'roles';

/** The confirmation dialog of one row action. */
function ActionDialog({
  action,
  user,
  onDone,
  onCancel,
  options,
  platform,
}: {
  action: Action;
  user: ManagedUser;
  onDone: (handover?: Handover) => void;
  onCancel: () => void;
  options: TenantOption[];
  platform: boolean;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [password, setPassword] = useState(() => generatePassword());
  const [name, setName] = useState(user.name ?? '');
  const [roles, setRoles] = useState<RolesValue>(() => rolesOf(user));
  const who = user.name ? `${user.name} (${user.email})` : user.email;

  async function run() {
    setBusy(true);
    setError(null);
    try {
      switch (action) {
        case 'disable':
          await api.updateUser(user.id, { disabled: true });
          break;
        case 'enable':
          await api.updateUser(user.id, { disabled: false });
          break;
        case 'rename':
          await api.updateUser(user.id, { name: name.trim() || null });
          break;
        case 'roles':
          await api.updateUser(user.id, rolesBody(roles, platform));
          break;
        case 'delete':
          await api.deleteUser(user.id);
          break;
        case 'reset':
          await api.resetUserPassword(user.id, password);
          onDone({ title: `New password for ${user.email}`, email: user.email, password });
          return;
      }
      onDone();
    } catch (err) {
      setError(new Error(userErrorText(err)));
      setBusy(false);
    }
  }

  const common = {
    busy,
    error,
    onCancel,
    onConfirm: () => {
      void run();
    },
  };
  switch (action) {
    case 'disable':
      return (
        <ConfirmDialog {...common} title="Disable user" confirmLabel="Disable" danger>
          <p>
            Disable <strong>{who}</strong>? The user is signed out everywhere at once and cannot
            sign in until enabled again. API tokens they created keep working; revoke them under API
            tokens if needed.
          </p>
        </ConfirmDialog>
      );
    case 'enable':
      return (
        <ConfirmDialog {...common} title="Enable user" confirmLabel="Enable">
          <p>
            Enable <strong>{who}</strong>? The user can sign in again with their password.
          </p>
        </ConfirmDialog>
      );
    case 'delete':
      return (
        <ConfirmDialog {...common} title="Delete user" confirmLabel="Delete" danger>
          <p>
            Delete <strong>{who}</strong> for good? The user is signed out at once. The audit log
            keeps their past actions. To only block the account, disable it instead.
          </p>
        </ConfirmDialog>
      );
    case 'rename':
      return (
        <ConfirmDialog {...common} title="Edit name" confirmLabel="Save">
          <label className="field">
            <span>Name of {user.email}</span>
            <input
              type="text"
              maxLength={200}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </label>
        </ConfirmDialog>
      );
    case 'roles':
      return (
        <ConfirmDialog
          {...common}
          title="Roles"
          confirmLabel="Save"
          onConfirm={() => {
            if (rolesError(roles) === null) void run();
          }}
        >
          <p>
            Roles of <strong>{who}</strong>. They apply from the next request of the user.
          </p>
          <RolesEditor value={roles} onChange={setRoles} options={options} platform={platform} />
          {rolesError(roles) && <p className="form-error">{rolesError(roles)}</p>}
        </ConfirmDialog>
      );
    case 'reset':
      return (
        <ConfirmDialog
          {...common}
          title="Reset password"
          confirmLabel="Set password"
          danger
          onConfirm={() => {
            if (newPasswordError(password) === null) void run();
          }}
        >
          <p>
            Set a new password for <strong>{who}</strong>. The user is signed out everywhere and
            needs the new password to sign in.
          </p>
          <PasswordField label="New password" value={password} onChange={setPassword} />
        </ConfirmDialog>
      );
  }
}

function When({ ts, now }: { ts: number | null; now: number }) {
  if (ts === null) return <span className="muted">never</span>;
  return (
    <time dateTime={new Date(ts).toISOString()} title={formatTimestamp(ts)}>
      {formatAge(ts, now)}
    </time>
  );
}

/**
 * Users (§10.3, ADR 0010): who can sign in to Kalvio and with which role.
 * Platform staff see everyone; tenant admins the users of their tenants.
 * Passwords are shown once, when they are set here.
 */
export function UsersPage() {
  const api = useApi();
  const { user: me } = useAuth();
  const now = useNow(30_000);
  const users = useResource('users', (s) => api.users(s));
  const { options, platform } = useTenantOptions();
  const [handover, setHandover] = useState<Handover | null>(null);
  const [dialog, setDialog] = useState<{ action: Action; user: ManagedUser } | null>(null);

  const list = users.data ? sortUsers(users.data) : undefined;

  return (
    <section>
      <div className="breadcrumbs">Settings /</div>
      <h1>Users</h1>
      <p className="muted">
        People who sign in to Kalvio and their roles: viewers read, operators also acknowledge
        alarms and edit rules, tenant admins also manage users and API tokens of their tenant.
        Disabling a user signs them out at once. To change your own password, use the page
        <Link to="/settings/password"> Change password</Link>.
      </p>
      {handover && (
        <HandoverPanel
          h={handover}
          onClose={() => {
            setHandover(null);
          }}
        />
      )}
      <div className="section-head">
        <h2>Add user</h2>
      </div>
      {options ? (
        <AddUserForm
          options={options}
          platform={platform}
          onCreated={(u, password) => {
            setHandover({ title: `User ${u.email} added`, email: u.email, password });
            users.reload();
          }}
        />
      ) : (
        <Spinner />
      )}
      <div className="section-head">
        <h2>Users</h2>
      </div>
      {users.error && <ErrorBox error={users.error} onRetry={users.reload} />}
      {!list ? (
        !users.error && <Spinner />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>E-mail</th>
                <th>Name</th>
                <th>Role</th>
                <th>State</th>
                <th>Last sign-in</th>
                <th>Sessions</th>
                <th>Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.map((u) => {
                const blocked = removalBlocked(u, list, me?.id);
                const open = (action: Action) => () => {
                  setDialog({ action, user: u });
                };
                return (
                  <tr key={u.id} className={u.disabled ? 'row-disabled muted' : undefined}>
                    <td className="strong">
                      {u.email}
                      {u.id === me?.id && <span className="muted small"> (you)</span>}
                    </td>
                    <td>{u.name ?? <span className="muted">—</span>}</td>
                    <td className="small">
                      {roleSummary(u).map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </td>
                    <td>
                      <span
                        className={`badge ${u.disabled ? 'badge-config-rejected' : 'badge-config-applied'}`}
                      >
                        {u.disabled ? 'Disabled' : 'Enabled'}
                      </span>
                    </td>
                    <td>
                      <When ts={u.last_login_at} now={now} />
                    </td>
                    <td>{u.active_sessions}</td>
                    <td>
                      <When ts={u.created_at} now={now} />
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn-small" onClick={open('rename')}>
                          Edit name
                        </button>
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={u.id === me?.id || !options}
                          title={u.id === me?.id ? 'Your own roles cannot be changed.' : undefined}
                          onClick={open('roles')}
                        >
                          Roles
                        </button>
                        <button type="button" className="btn btn-small" onClick={open('reset')}>
                          Reset password
                        </button>
                        {u.disabled ? (
                          <button type="button" className="btn btn-small" onClick={open('enable')}>
                            Enable
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-small btn-danger-outline"
                            disabled={blocked !== null}
                            title={blocked ?? undefined}
                            onClick={open('disable')}
                          >
                            Disable
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-small btn-danger-outline"
                          disabled={blocked !== null}
                          title={blocked ?? undefined}
                          onClick={open('delete')}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {dialog && (
        <ActionDialog
          key={`${dialog.action}:${dialog.user.id}`}
          action={dialog.action}
          user={dialog.user}
          options={options ?? []}
          platform={platform}
          onCancel={() => {
            setDialog(null);
          }}
          onDone={(h) => {
            setDialog(null);
            if (h) setHandover(h);
            users.reload();
          }}
        />
      )}
    </section>
  );
}
